import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderStatusPage from './pages/OrderStatusPage';
import AuthModal from './components/AuthModal';
import LocationCheck from './components/LocationCheck';
import { restaurantAPI } from './services/api';
import { deliveryAuth } from './services/auth';

function App() {
  const [restaurantConfig, setRestaurantConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [deliveryInfo, setDeliveryInfo] = useState<any>(null);

  useEffect(() => {
    loadRestaurantConfig();
    // Check if user is already logged in
    const user = deliveryAuth.getCurrentUser();
    setCurrentUser(user);
  }, []);

  const loadRestaurantConfig = async () => {
    try {
      const result = await restaurantAPI.getConfig();
      setRestaurantConfig(result.data);
    } catch (error) {
      console.error('Failed to load restaurant config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (user: any) => {
    setCurrentUser(user);
    setShowAuthModal(false);
  };

  const handleLogout = () => {
    deliveryAuth.logout();
    setCurrentUser(null);
  };

  const requireAuth = () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  const handleLocationVerified = (available: boolean, distance: number, deliveryFee: number, zone: any) => {
    setDeliveryInfo({
      available,
      distance,
      deliveryFee,
      zone,
      verified: true
    });
  };

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Show location check if restaurant has location data and delivery info not verified
  if (restaurantConfig?.location?.latitude && 
      restaurantConfig?.location?.longitude && 
      (!deliveryInfo || !deliveryInfo.verified)) {
    return (
      <Router>
        <LocationCheck
          restaurantLocation={restaurantConfig.location}
          onLocationVerified={handleLocationVerified}
        />
        <Toaster position="top-right" />
      </Router>
    );
  }

  return (
    <Router>
      <div className="container">
        <Routes>
          <Route 
            path="/" 
            element={
              <HomePage 
                restaurantConfig={restaurantConfig}
                currentUser={currentUser}
                onAuthRequired={() => setShowAuthModal(true)}
                onLogout={handleLogout}
              />
            } 
          />
          <Route 
            path="/menu" 
            element={
              <MenuPage 
                currentUser={currentUser}
                onAuthRequired={requireAuth}
              />
            } 
          />
          <Route 
            path="/cart" 
            element={
              <CartPage 
                currentUser={currentUser}
                onAuthRequired={requireAuth}
                deliveryInfo={deliveryInfo}
              />
            } 
          />
          <Route 
            path="/checkout" 
            element={
              <CheckoutPage 
                currentUser={currentUser}
                onAuthRequired={requireAuth}
                deliveryInfo={deliveryInfo}
              />
            } 
          />
          <Route path="/order/:orderId" element={<OrderStatusPage />} />
        </Routes>
        
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
        
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;